import DeleteIcon from "../../../assets/icons/DeleteIcon";
import {
  CreatorMusicRemoveDesktopIcon,
  CreatorMusicRemoveMobileIcon,
} from "../../../assets/icons/CreatorMusicIcons";
import MusicAddIcon from "../../../assets/icons/MusicAddIcon";
import MusicPauseIcon from "../../../assets/icons/MusicPauseIcon";
import MusicPlayIcon from "../../../assets/icons/MusicPlayIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import LoaderDark from "../../common/LoaderDark/LoaderDark";
import "./CreatorMusic.css";
import gluedin from "gluedin-shorts-js";
import { debounce } from "lodash";
import React, { useEffect, useState, useRef, useMemo } from "react";
import useIsMobile from "../../../app/hooks/useIsMobile";

const soundModule = new gluedin.GluedInSoundModule();

function CreatorMusic({
  onMusicSelect,
  setMusicToolSelected,
  audioFile,
}: {
  onMusicSelect?: (track: any) => void;
  setMusicToolSelected?: any;
  audioFile?:any;
}) {
  const [musicList, setMusicList] = useState<any[]>([]);
  const [playingTrack, setPlayingTrack] = useState<string | null>(null);
  //   const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const [selectedMusic, setSelectedMusic] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults]: any = useState([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await soundModule.getSoundList("");
        const { data: { result = [] } = {} } = response;
        setMusicList(result);
      } catch (error) {
        console.error("Error fetching music list:", error);
      }
      setIsLoading(false);
    }
    fetchData();

    return () => {
      // Stop the audio when component unmounts
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingTrack(null);
    };
  }, []);

  // Debounce function for search filtering
  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        setIsLoading(true);
        const filteredList = musicList.filter((item: any) =>
          item?.name.toLowerCase().includes(term.toLowerCase())
        );
        setSearchResults(filteredList);
        setIsLoading(false);
      }, 500),
    [musicList]
  ); // Memoize the debounce function with 'data' dependency

  useEffect(() => {
    if (searchTerm) {
      debouncedSearch(searchTerm);
    } else {
      setSearchResults(musicList);
    }
    return () => debouncedSearch.cancel(); // Cleanup on unmount
  }, [searchTerm, musicList, debouncedSearch]);

  const handlePlayPause = (trackUrl: string, trackId: string) => {
    if (playingTrack === trackId) {
      //   audio?.pause();
      audioRef.current?.pause();
      setPlayingTrack(null);
    } else {
      //   audio?.pause();
      audioRef.current?.pause(); // Pause the previous audio if it exists
      const newAudio = new Audio(trackUrl);
      newAudio.play();
      audioRef.current = newAudio;
      //   setAudio(newAudio);
      setPlayingTrack(trackId);

      newAudio.onended = () => setPlayingTrack(null);
    }
  };

  const handleAddMusic = (track: any) => {
    // Stop any currently playing music
    // audio?.pause();
    // setPlayingTrack(null);

    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingTrack(null);

    setSelectedMusic(track);
    onMusicSelect?.(track); // Call the function only if it exists
    setMusicToolSelected(true);
  };

  const handleRemoveMusic = (trackId: string) => {
    if (audioFile?.soundId === trackId) {
      setSelectedMusic(null);
      onMusicSelect?.(null);
      setMusicToolSelected(false);
    }
  };

  const handleSearchInputChange = (event: any) => {
    setSearchTerm(event.target.value);
  };

  return (
    <>
      <div className="full-box">
        <div className="page-search-box">
          <div className="input-box">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchInputChange}
              name="search"
              placeholder="Search Music..."
              autoComplete="off"
            />
            <SearchIcon />
          </div>
        </div>
      </div>
      <div className="music-container mt-4">
        <ul>
          {isLoading && searchResults?.length === 0 && <LoaderDark />}

          {!isLoading &&
            searchResults?.map((track: any) => (
              <li key={track.soundId} className="music-item">
                <div className="music-item-content">
                  <img
                    src={track.thumbnail}
                    alt={track.name}
                    width={70}
                    height={70}
                  />
                  <div className="music-item-name">
                    <h2>{track.name}</h2>
                    <span>{track.artist}</span>
                  </div>
                </div>

                <div className="music-item-actions">
                  {audioFile?.soundId === track?.soundId ? (
                    <button onClick={() => handleRemoveMusic(track.soundId)}>
                      {isMobile ? (
                        <CreatorMusicRemoveMobileIcon />
                      ) : (
                        <CreatorMusicRemoveDesktopIcon />
                      )}
                    </button>
                  ) : (
                    <button onClick={() => handleAddMusic(track)}>
                      <MusicAddIcon />
                    </button>
                  )}
                  <button
                    onClick={() =>
                      handlePlayPause(track.soundUrl, track.soundId)
                    }
                  >
                    {playingTrack === track.soundId ? (
                      <MusicPauseIcon />
                    ) : (
                      <MusicPlayIcon />
                    )}
                  </button>
                </div>
              </li>
            ))}
        </ul>
      </div>
    </>
  );
}

export default CreatorMusic;
